/**
 * PhotoMarkerLayer Component
 *
 * Displays geotagged photos as markers on the map.
 * Each marker shows a camera icon and clicking it displays the photo.
 * Photos at the same location are grouped with slideshow navigation.
 */

import { useState, useMemo, useRef, useCallback } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { GeoPhoto, formatRelativeTime } from '../types/events';

/**
 * Custom hook for touch swipe detection.
 */
function useSwipe(onSwipeLeft: () => void, onSwipeRight: () => void, threshold = 50) {
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
    // Prevent map from receiving touch events
    e.stopPropagation();
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    // Prevent map panning while swiping
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const onTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - go to next
        onSwipeLeft();
      } else {
        // Swiped right - go to previous
        onSwipeRight();
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [onSwipeLeft, onSwipeRight, threshold]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}

/**
 * Wrapper component for swipeable photo/video in popup.
 */
function SwipeablePhotoContainer({
  children,
  hasMultiple,
  onSwipeLeft,
  onSwipeRight,
  onClick,
}: {
  children: React.ReactNode;
  hasMultiple: boolean;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  onClick: () => void;
}) {
  const swipeHandlers = useSwipe(onSwipeLeft, onSwipeRight);

  return (
    <div
      style={{
        marginBottom: '12px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        cursor: 'pointer',
        touchAction: 'pan-y',
      }}
      onClick={onClick}
      onTouchStart={hasMultiple ? swipeHandlers.onTouchStart : undefined}
      onTouchMove={hasMultiple ? swipeHandlers.onTouchMove : undefined}
      onTouchEnd={hasMultiple ? swipeHandlers.onTouchEnd : undefined}
      title={hasMultiple ? 'Swipe to browse, tap to enlarge' : 'Tap to view full size'}
    >
      {children}
    </div>
  );
}

interface PhotoMarkerLayerProps {
  photos: GeoPhoto[];
  onRequestRemoval?: (photoId: string, fileName: string) => void;
}

interface PhotoGroup {
  key: string;
  lat: number;
  lon: number;
  photos: GeoPhoto[];
}

/**
 * Create a camera icon for photo markers.
 * Shows count badge if multiple photos at location.
 */
function createPhotoIcon(count: number): L.DivIcon {
  const badge = count > 1 ? `
    <div style="
      position: absolute;
      top: -4px;
      right: -4px;
      background-color: #dc2626;
      color: white;
      font-size: 10px;
      font-weight: bold;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
    ">${count}</div>
  ` : '';

  return L.divIcon({
    className: 'photo-marker',
    html: `
      <div style="
        position: relative;
        background-color: #8b5cf6;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        border: 3px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        cursor: pointer;
      ">
        📷
        ${badge}
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18],
  });
}

/**
 * Round coordinates to group nearby photos (within ~10m).
 */
function getLocationKey(lat: number, lon: number): string {
  // Round to 4 decimal places (~11m precision)
  return `${lat.toFixed(4)},${lon.toFixed(4)}`;
}

/**
 * Check if a file is a video based on its URL or filename.
 */
function isVideoFile(url: string, fileName: string): boolean {
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
  const lowerUrl = url.toLowerCase();
  const lowerFileName = fileName.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.endsWith(ext) || lowerFileName.endsWith(ext));
}

/**
 * Lightbox component for full-screen photo/video viewing with slideshow support.
 */
function PhotoLightbox({
  photos,
  currentIndex,
  onClose,
  onPrev,
  onNext,
}: {
  photos: GeoPhoto[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const photo = photos[currentIndex];
  const hasMultiple = photos.length > 1;
  const isVideo = isVideoFile(photo.url, photo.fileName);

  // Touch swipe support for mobile
  const swipeHandlers = useSwipe(onNext, onPrev);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        touchAction: 'none', // Prevent all touch actions from reaching elements below
      }}
      onClick={onClose}
      onTouchStart={(e) => {
        e.stopPropagation();
        if (hasMultiple) swipeHandlers.onTouchStart(e);
      }}
      onTouchMove={(e) => {
        e.stopPropagation();
        e.preventDefault();
        if (hasMultiple) swipeHandlers.onTouchMove(e);
      }}
      onTouchEnd={(e) => {
        e.stopPropagation();
        if (hasMultiple) swipeHandlers.onTouchEnd();
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: '44px',
          height: '44px',
          fontSize: '24px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
        }}
        title="Close"
      >
        ✕
      </button>

      {/* Navigation arrows for multiple photos */}
      {hasMultiple && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            style={{
              position: 'absolute',
              left: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
            }}
            title="Previous"
          >
            ◀
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            style={{
              position: 'absolute',
              right: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '50px',
              height: '50px',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
            }}
            title="Next"
          >
            ▶
          </button>
        </>
      )}

      {/* Photo or Video */}
      {isVideo ? (
        <video
          src={photo.url}
          controls
          autoPlay
          style={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={photo.url}
          alt={photo.fileName}
          style={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            objectFit: 'contain',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {/* Photo info overlay */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '14px',
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {hasMultiple && (
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>
            {currentIndex + 1} / {photos.length}
          </div>
        )}
        <div style={{ fontWeight: '600', marginBottom: '4px' }}>{photo.fileName}</div>
        <div style={{ fontSize: '12px', opacity: 0.8 }}>
          {photo.lat.toFixed(5)}, {photo.lon.toFixed(5)}
          {photo.takenAt && ` • ${new Date(photo.takenAt).toLocaleString()}`}
        </div>
      </div>
    </div>
  );
}

export default function PhotoMarkerLayer({ photos, onRequestRemoval }: PhotoMarkerLayerProps) {
  const [lightboxGroup, setLightboxGroup] = useState<PhotoGroup | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const [groupIndices, setGroupIndices] = useState<Record<string, number>>({});

  // Group photos by location
  const photoGroups = useMemo(() => {
    const groups: Record<string, PhotoGroup> = {};

    photos.forEach((photo) => {
      const key = getLocationKey(photo.lat, photo.lon);
      if (!groups[key]) {
        groups[key] = {
          key,
          lat: photo.lat,
          lon: photo.lon,
          photos: [],
        };
      }
      groups[key].photos.push(photo);
    });

    return Object.values(groups);
  }, [photos]);

  if (photos.length === 0) return null;

  const getCurrentIndex = (groupKey: string) => groupIndices[groupKey] || 0;

  const setCurrentIndex = (groupKey: string, index: number) => {
    setGroupIndices(prev => ({ ...prev, [groupKey]: index }));
  };

  const goToPrevPhoto = (group: PhotoGroup) => {
    const currentIdx = getCurrentIndex(group.key);
    const newIdx = currentIdx > 0 ? currentIdx - 1 : group.photos.length - 1;
    setCurrentIndex(group.key, newIdx);
  };

  const goToNextPhoto = (group: PhotoGroup) => {
    const currentIdx = getCurrentIndex(group.key);
    const newIdx = currentIdx < group.photos.length - 1 ? currentIdx + 1 : 0;
    setCurrentIndex(group.key, newIdx);
  };

  return (
    <>
      {photoGroups.map((group) => {
        const currentIdx = getCurrentIndex(group.key);
        const currentPhoto = group.photos[currentIdx];
        const hasMultiple = group.photos.length > 1;

        return (
          <Marker
            key={group.key}
            position={[group.lat, group.lon]}
            icon={createPhotoIcon(group.photos.length)}
            zIndexOffset={-500}
          >
            <Popup maxWidth={320} minWidth={280} className="photo-popup">
              <div className="photo-popup-content" style={{ textAlign: 'center', width: '280px' }}>
                {/* Slideshow navigation header */}
                {hasMultiple && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    padding: '4px 8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                  }}>
                    <button
                      onClick={() => goToPrevPhoto(group)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      ◀
                    </button>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#4b5563',
                    }}>
                      {currentIdx + 1} / {group.photos.length}
                    </span>
                    <button
                      onClick={() => goToNextPhoto(group)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                      }}
                    >
                      ▶
                    </button>
                  </div>
                )}

                {/* Photo/Video - clickable for lightbox, swipeable */}
                <SwipeablePhotoContainer
                  hasMultiple={hasMultiple}
                  onSwipeLeft={() => goToNextPhoto(group)}
                  onSwipeRight={() => goToPrevPhoto(group)}
                  onClick={() => {
                    setLightboxGroup(group);
                    setLightboxIndex(currentIdx);
                  }}
                >
                  {isVideoFile(currentPhoto.url, currentPhoto.fileName) ? (
                    <div style={{
                      position: 'relative',
                      width: '100%',
                      height: '200px',
                      backgroundColor: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <video
                        src={currentPhoto.url}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          objectFit: 'contain',
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(139, 92, 246, 0.9)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                      }}>
                        ▶
                      </div>
                    </div>
                  ) : (
                    <img
                      src={currentPhoto.url}
                      alt={currentPhoto.fileName}
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    padding: '6px',
                    fontSize: '11px',
                    color: '#6b7280',
                  }}>
                    {hasMultiple ? '👆 Swipe to browse • Tap to enlarge' : '🔍 Click to enlarge'}
                  </div>
                </SwipeablePhotoContainer>

                {/* Photo Info */}
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '8px',
                }}>
                  <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                    {currentPhoto.fileName}
                  </div>
                  {currentPhoto.takenAt && (
                    <div>Taken: {new Date(currentPhoto.takenAt).toLocaleString()}</div>
                  )}
                  <div style={{ marginTop: '4px' }}>
                    <span style={{
                      backgroundColor: '#8b5cf6',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '11px',
                    }}>
                      {currentPhoto.lat.toFixed(5)}, {currentPhoto.lon.toFixed(5)}
                    </span>
                  </div>
                  {currentPhoto.altitude && (
                    <div style={{ marginTop: '4px' }}>
                      Altitude: {Math.round(currentPhoto.altitude)}m
                    </div>
                  )}
                  <div style={{ marginTop: '4px', color: '#9ca3af', fontSize: '11px' }}>
                    Added {formatRelativeTime(currentPhoto.addedAt)}
                  </div>
                </div>

                {/* Request Removal Button */}
                {onRequestRemoval && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRequestRemoval(currentPhoto.id, currentPhoto.fileName);
                    }}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#fef3c7',
                      color: '#d97706',
                      border: '1px solid #fcd34d',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                  >
                    Request Removal
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Lightbox overlay */}
      {lightboxGroup && (
        <PhotoLightbox
          photos={lightboxGroup.photos}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxGroup(null)}
          onPrev={() => {
            const newIdx = lightboxIndex > 0 ? lightboxIndex - 1 : lightboxGroup.photos.length - 1;
            setLightboxIndex(newIdx);
          }}
          onNext={() => {
            const newIdx = lightboxIndex < lightboxGroup.photos.length - 1 ? lightboxIndex + 1 : 0;
            setLightboxIndex(newIdx);
          }}
        />
      )}
    </>
  );
}
